import { useEffect, useState, type FormEvent } from "react"
import { Send01 } from "@untitledui/icons"
import { toast } from "sonner"
import { useInviteMember } from "@/lib/api/use-teams"
import { ApiError } from "@/lib/api/client"
import type { TeamType } from "@/lib/api/types"
import { ROLES_BY_TEAM_TYPE } from "@/lib/team/roles"
import { DesignDialog } from "@/components/ui/design-dialog"
import { DesignSelect } from "@/components/ui/design-select"
import {
  Field,
  onboardingInputClass,
  onboardingInputErrorClass,
} from "@/components/onboarding/onboarding-shell"
import { cx } from "@/lib/utils/cx"

interface InviteMemberDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  teamId: number | null
  teamName: string | null
  teamType: TeamType
}

export function InviteMemberDialog({
  open,
  onOpenChange,
  teamId,
  teamName,
  teamType,
}: InviteMemberDialogProps) {
  const roleOptions = ROLES_BY_TEAM_TYPE[teamType]
  const inviteMutation = useInviteMember(teamId)

  const [firstName, setFirstName] = useState("")
  const [lastName, setLastName] = useState("")
  const [email, setEmail] = useState("")
  const [role, setRole] = useState<string>(roleOptions[0])
  const [errors, setErrors] = useState<{ email?: string }>({})

  // Reset role when team type flips so we never submit a stale, now-invalid one.
  useEffect(() => {
    if (!roleOptions.includes(role as (typeof roleOptions)[number])) {
      setRole(roleOptions[0])
    }
  }, [roleOptions, role])

  // Reset the form whenever the dialog re-opens — opening a fresh invite
  // shouldn't carry the previous draft across teams.
  useEffect(() => {
    if (open) {
      setFirstName("")
      setLastName("")
      setEmail("")
      setRole(roleOptions[0])
      setErrors({})
    }
  }, [open, roleOptions])

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault()
    if (teamId == null) {
      toast.error("No team selected. Pick a team first.")
      return
    }
    if (!email.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())) {
      setErrors({ email: "Enter a valid email" })
      return
    }
    try {
      await inviteMutation.mutateAsync({
        email: email.trim(),
        role,
        ...(firstName.trim() && { firstName: firstName.trim() }),
        ...(lastName.trim() && { lastName: lastName.trim() }),
      })
      toast.success("Invite sent")
      onOpenChange(false)
    } catch (err) {
      const msg = err instanceof ApiError ? err.message : "Couldn't send invite"
      toast.error(msg)
    }
  }

  return (
    <DesignDialog.Root open={open} onOpenChange={onOpenChange}>
      <DesignDialog.Content width="md">
        <DesignDialog.Header>
          <DesignDialog.Title>Invite a team member</DesignDialog.Title>
          {teamName && (
            <DesignDialog.Description>
              Inviting to{" "}
              <span className="font-semibold text-zinc-700">{teamName}</span>
            </DesignDialog.Description>
          )}
        </DesignDialog.Header>
        <form onSubmit={handleSubmit}>
          <DesignDialog.Body className="space-y-5">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <Field label="First name">
                <input
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                  placeholder="Alex"
                  maxLength={255}
                  className={onboardingInputClass}
                />
              </Field>
              <Field label="Last name">
                <input
                  value={lastName}
                  onChange={(e) => setLastName(e.target.value)}
                  placeholder="Garcia"
                  maxLength={255}
                  className={onboardingInputClass}
                />
              </Field>
            </div>

            <Field label="Email" required error={errors.email}>
              <input
                type="email"
                required
                value={email}
                onChange={(e) => {
                  setEmail(e.target.value)
                  if (errors.email) setErrors({})
                }}
                placeholder="alex@company.com"
                className={cx(onboardingInputClass, errors.email && onboardingInputErrorClass)}
                autoFocus
              />
            </Field>

            <Field label="Role" required>
              <DesignSelect.Root value={role} onValueChange={setRole}>
                <DesignSelect.Trigger
                  className={cx(
                    onboardingInputClass,
                    "text-left flex items-center justify-between",
                    "data-[state=open]:border-emerald-500 data-[state=open]:ring-4 data-[state=open]:ring-emerald-500/10",
                  )}
                >
                  <DesignSelect.Value placeholder="Pick a role" />
                </DesignSelect.Trigger>
                <DesignSelect.Content>
                  {roleOptions.map((r) => (
                    <DesignSelect.Item key={r} value={r}>
                      {r}
                    </DesignSelect.Item>
                  ))}
                </DesignSelect.Content>
              </DesignSelect.Root>
            </Field>
          </DesignDialog.Body>
          <DesignDialog.Footer>
            <DesignDialog.Close asChild>
              <button
                type="button"
                className="h-12 px-5 rounded-xl text-[14px] font-semibold text-zinc-600 hover:text-zinc-900 hover:bg-zinc-100 transition-all duration-150"
              >
                Cancel
              </button>
            </DesignDialog.Close>
            <button
              type="submit"
              disabled={inviteMutation.isPending || teamId == null}
              className="h-12 px-7 inline-flex items-center gap-2 rounded-xl bg-zinc-900 text-white text-[15px] font-semibold shadow-lg shadow-zinc-900/20 hover:bg-black hover:shadow-zinc-900/30 transition-all duration-150 disabled:opacity-40 disabled:cursor-not-allowed disabled:shadow-none"
            >
              <Send01 className="size-4" aria-hidden />
              {inviteMutation.isPending ? "Sending…" : "Send invite"}
            </button>
          </DesignDialog.Footer>
        </form>
      </DesignDialog.Content>
    </DesignDialog.Root>
  )
}
